import { useCallback, useEffect, useState } from "react";
import { Sparkles, Check, EyeOff, X, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminDesignQueue, adminModerateDesign } from "../../lib/api";

/**
 * Admin → Designs moderation queue.
 * Tabs: Pending / Published / Hidden / All.
 * Actions per row: Publish (approve), Hide (publicly), Reject (back to private).
 */
const STATUS_TABS = [
  { k: "pending", l: "Pending", cls: "text-amber-400" },
  { k: "published", l: "Published", cls: "text-emerald-400" },
  { k: "hidden", l: "Hidden", cls: "text-zinc-500" },
  { k: "all", l: "All", cls: "text-zinc-300" },
];

export default function DesignsTab({ pw }) {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminDesignQueue(pw, status === "all" ? undefined : status);
      setRows(r || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load designs");
    } finally { setLoading(false); }
  }, [pw, status]);

  useEffect(() => { reload(); }, [reload]);

  const moderate = async (did, action) => {
    try {
      await adminModerateDesign(did, action, pw);
      toast.success(`Design ${action === "publish" ? "published" : action === "hide" ? "hidden" : "rejected"}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div data-testid="designs-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl uppercase flex items-center gap-3">
            <Sparkles size={22} className="text-[var(--tc-red)]" /> Design Gallery Queue
          </h2>
          <p className="font-mono text-xs text-zinc-500 mt-1">
            Customers opt in via the Save dialog. Approve to publish to <code className="text-zinc-300">/gallery/designs</code>.
          </p>
        </div>
        <button onClick={reload} data-testid="designs-refresh" className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-10">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="design-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`design-status-${t.k}`}
            onClick={() => setStatus(t.k)}
            className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest uppercase ${
              status === t.k ? "bg-[var(--tc-red)] border-[var(--tc-red)] text-white" : "border-[#222] text-zinc-400 hover:text-white"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <Sparkles size={32} className="mx-auto mb-3 text-zinc-700" />
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">No designs in this bucket.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="designs-grid">
          {rows.map((d) => (
            <div key={d.id} className="surface-card overflow-hidden" data-testid={`design-${d.id}`}>
              <div className="aspect-square bg-[#050505] border-b border-[#222] flex items-center justify-center">
                {d.preview_image ? (
                  <img src={d.preview_image} alt={d.name} className="w-full h-full object-contain" />
                ) : (
                  <Sparkles size={32} className="text-zinc-700" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-lg uppercase leading-tight">{d.name}</h3>
                  <span className={`font-mono text-[9px] tracking-widest uppercase ${
                    d.public_status === "published" ? "text-emerald-400"
                    : d.public_status === "hidden" ? "text-zinc-500" : "text-amber-400"
                  }`}>{d.public_status}</span>
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-1">
                  by {d.user_name || "—"} · <span className="text-zinc-600">{d.user_email}</span>
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-1">
                  {d.product_name || d.product_mockup_type} · {d.layers?.length || 0} layer{(d.layers?.length || 0) === 1 ? "" : "s"} · likes {d.likes || 0} · remixed {d.remix_count || 0}×
                </div>
                <div className="flex gap-1.5 mt-3 flex-wrap">
                  {d.public_status !== "published" && (
                    <button
                      data-testid={`design-publish-${d.id}`}
                      onClick={() => moderate(d.id, "publish")}
                      className="border border-emerald-700 hover:bg-emerald-950 text-emerald-300 px-2.5 py-1 font-mono text-[10px] tracking-widest flex items-center gap-1"
                    ><Check size={11} /> APPROVE</button>
                  )}
                  {d.public_status !== "hidden" && d.public_status !== "private" && (
                    <button
                      data-testid={`design-hide-${d.id}`}
                      onClick={() => moderate(d.id, "hide")}
                      className="border border-[#333] hover:bg-[#111] text-zinc-300 px-2.5 py-1 font-mono text-[10px] tracking-widest flex items-center gap-1"
                    ><EyeOff size={11} /> HIDE</button>
                  )}
                  <button
                    data-testid={`design-reject-${d.id}`}
                    onClick={() => moderate(d.id, "reject")}
                    className="border border-red-900 hover:bg-red-950 text-red-300 px-2.5 py-1 font-mono text-[10px] tracking-widest flex items-center gap-1"
                  ><X size={11} /> REJECT</button>
                  {d.public_status === "published" && (
                    <a
                      href={`/gallery/designs?d=${d.id}`}
                      target="_blank" rel="noreferrer"
                      className="ml-auto font-mono text-[10px] text-zinc-400 hover:text-white flex items-center gap-1"
                    ><ExternalLink size={10} /> VIEW</a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
